import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Mail, Phone } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const links = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/projects", label: "Projects" },
  { to: "/videos", label: "Videos" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  // Pages that have a full-bleed dark hero — nav text needs to be light here.
  const DARK_HERO_PAGES = ["/", "/videos"];
  const onDark = !scrolled && DARK_HERO_PAGES.includes(location.pathname);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-b border-black/10" : "bg-transparent"
      }`}
    >
      <div data-testid="top-strip" className={`bg-[#0e5f84] text-white overflow-hidden transition-all duration-500 ${scrolled ? "max-h-0 opacity-0" : "max-h-12 opacity-100"}`}>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-2 flex items-center justify-between font-mono-accent text-[11px] uppercase tracking-widest">
          <span className="hidden sm:block text-white/80">ISO 9001:2015 Certified · Make in India</span>
          <div className="flex items-center gap-5">
            <a href="mailto:sales@manikindia.com" className="flex items-center gap-2 hover:text-[#78be27] transition-colors"><Mail size={13} /> <span className="hidden xs:inline sm:inline">sales@manikindia.com</span></a>
            <a href="tel:+918424040899" className="flex items-center gap-2 hover:text-[#78be27] transition-colors"><Phone size={13} /> +91 84240 40899</a>
            <a href="tel:+919845172851" data-testid="nav-phone-secondary" className="hidden md:flex items-center gap-2 hover:text-[#78be27] transition-colors"><Phone size={13} /> +91 98451 72851</a>
          </div>
        </div>
      </div>
      <div className={`mx-auto max-w-[1400px] px-6 lg:px-10 flex items-center justify-between transition-[padding] duration-500 ${scrolled ? "py-3" : "py-4"}`}>
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-4">
          <img src="/assets/manik-logo.png" alt="Manik Engineering" className="h-11 w-auto" />
          <span className={`hidden md:block w-px h-8 ${onDark ? "bg-white/30" : "bg-black/15"}`} />
          <span className={`hidden md:block font-display font-extrabold text-xl uppercase tracking-tight whitespace-nowrap leading-none ${onDark ? "text-white" : "text-black"}`}>
            Manik Engineering Pvt. Ltd.
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-mono-accent text-xs uppercase tracking-widest link-underline pb-1 ${
                  isActive ? "text-[#3da8d8]" : onDark ? "text-white/90" : "text-[#334155]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta"
            className={`btn-fill border px-6 py-3 font-mono-accent text-xs uppercase tracking-widest hover:text-white ${onDark ? "border-white/70 text-white" : "border-[#3da8d8] text-[#3da8d8]"}`}
          >
            Enquire Now
          </Link>
        </nav>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          className={`lg:hidden ${onDark ? "text-white" : "text-[#334155]"}`}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white border-t border-black/10 mt-3"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="font-display text-2xl text-[#334155]"
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
